// src/pages/ProductMonAppliClubLight/AboutMonAppliClubLight.jsx
import React, { useMemo } from "react";
import {
  FaGlobe,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaUserShield,
} from "react-icons/fa";

const cx = (...c) => c.filter(Boolean).join(" ");

const FeatureCard = ({ title, items, accent, icon: Icon }) => (
  <div
    className={cx(
      "rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg",
      "dark:border-white/10 dark:bg-slate-900"
    )}
  >
    <div className="flex items-start gap-3">
      <div
        className={cx(
          "grid h-11 w-11 shrink-0 place-items-center rounded-2xl text-white",
          "bg-gradient-to-r",
          accent
        )}
      >
        <Icon />
      </div>

      <div>
        <div className="text-lg font-semibold text-slate-900 dark:text-white">
          {title}
        </div>
        <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Fonctions principales.
        </div>
      </div>
    </div>

    <ul className="mt-5 space-y-2 text-sm text-slate-700 dark:text-slate-200">
      {items.map((t) => (
        <li key={t} className="flex gap-2">
          <FaCheckCircle className="mt-0.5 text-emerald-500" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  </div>
);

const AboutMonAppliClubLight = ({ onDemoClick }) => {
  const blocks = useMemo(
    () => [
      {
        title: "Visiteurs & adhérents",
        icon: FaGlobe,
        accent: "from-sky-500 to-cyan-500",
        items: [
          "Présentation du club et disciplines enseignées",
          "Groupes, horaires et tarifs",
          "Actualités, albums photos et équipe",
          "Formulaire de contact et inscriptions en ligne",
        ],
      },
      {
        title: "Administration",
        icon: FaUserShield,
        accent: "from-emerald-500 to-green-600",
        items: [
          "Tableau de bord administrateur",
          "Gestion des inscriptions et messages",
          "Gestion des pages, équipes, articles et albums",
          "Personnalisation des contenus du site",
        ],
      },
    ],
    []
  );

  return (
    <section className="bg-white py-16 sm:py-20 dark:bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-6 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-7">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                Fonctionnalités
              </div>

              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
                Une solution légère pour présenter votre club efficacement
              </h2>

              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                MonAppliClub Light met l’accent sur l’essentiel : donner une
                image moderne à votre club, présenter les bonnes informations et
                faciliter le contact avec vos visiteurs et adhérents.
              </p>

              <div className="mt-6 overflow-hidden rounded-[28px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
                <img
                  src="/images/appli_tel02.webp"
                  alt="MonAppliClub Light"
                  className="w-full object-cover"
                  loading="lazy"
                />
              </div>

              <div className="mt-6 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                {[
                  "Site responsive : mobile, tablette et PC",
                  "Navigation claire pour les visiteurs",
                  "Espace d’administration simple à gérer",
                ].map((item) => (
                  <div key={item} className="flex gap-3">
                    <FaCheckCircle className="mt-0.5 text-sky-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={onDemoClick}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-sky-500 px-6 py-3 text-sm font-semibold text-white transition hover:bg-sky-600"
              >
                Tester la démo <FaExternalLinkAlt className="text-sm" />
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="max-w-3xl">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-sky-500">
                À quoi sert la version Light ?
              </div>
              <h3 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-white">
                Aller à l’essentiel avec un site clair, moderne et facile à piloter
              </h3>
              <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
                Cette version est idéale pour les clubs qui veulent surtout
                valoriser leur activité en ligne, diffuser les bonnes
                informations et gérer simplement leur vitrine web.
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              {blocks.map((block) => (
                <FeatureCard
                  key={block.title}
                  title={block.title}
                  items={block.items}
                  accent={block.accent}
                  icon={block.icon}
                />
              ))}
            </div>

            <div className="mt-6 overflow-hidden rounded-[32px] border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
              <img
                src="/images/tablette_angle2.webp"
                alt="Aperçu tablette MonAppliClub Light"
                className="w-full rounded-[24px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMonAppliClubLight;